import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';

import { cn } from '../lib/utils';
import { BsTooltip } from './tooltip';

export interface TabRadioOption {
  value: string;
  label: React.ReactNode;
  tooltips?: string;
  className?: string;
}

export interface TabRadioGroupProps extends Omit<
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>,
  'children'
> {
  options: TabRadioOption[];
}

const TabRadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  TabRadioGroupProps
>(({ className, options, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      ref={ref}
      className={cn(
        'flex flex-col items-start justify-center p-[2px] rounded-[4px] bg-[#EFF0F6]',
        className
      )}
      {...props}
    >
      <div className="flex gap-[2px] items-center w-full">
        {options.map((option) => (
          <TabRadioItem
            key={option.value}
            value={option.value}
            tooltips={option.tooltips}
            className={option.className}
          >
            {option.label}
          </TabRadioItem>
        ))}
      </div>
    </RadioGroupPrimitive.Root>
  );
});
TabRadioGroup.displayName = 'TabRadioGroup';

interface TabRadioItemProps extends React.ComponentPropsWithoutRef<
  typeof RadioGroupPrimitive.Item
> {
  children: React.ReactNode;
  tooltips?: string;
  className?: string;
}

const TabRadioItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  TabRadioItemProps
>(({ className, children, tooltips, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        'flex-1 h-[30px] flex items-center justify-center px-[10px] py-[7px] rounded-[4px] text-[14px] text-[#121111] cursor-pointer',
        'border border-solid border-[#EFF0F6] bg-transparent',
        'transition-all ease-out duration-200',
        'outline-none',
        'hover:text-[#121111] hover:border-[#E0E0E0] hover:bg-white',
        'data-[state=checked]:text-[#0265FF] data-[state=checked]:border-[#E0E0E0] data-[state=checked]:bg-white',
        className
      )}
      {...props}
    >
      {tooltips ? <BsTooltip content={tooltips}>{children}</BsTooltip> : children}
    </RadioGroupPrimitive.Item>
  );
});
TabRadioItem.displayName = 'TabRadioItem';

export { TabRadioGroup, TabRadioItem };
